import * as React from "react";
import { useUser } from "../globalState";
import { useActiveInfluencer, useUnsignedCount } from "../hooks";
import { NAV_LINKS, getIconComponent, ADMIN_SECTIONS } from "./config";
import { SECTION_META } from "./sections";
import { Badge } from "../themeV2";

/**
 * Resolves a link's path, which can be a string or a function of guard context.
 */
const resolvePath = (link, guardCtx) =>
  typeof link.path === "function" ? link.path(guardCtx) : link.path;

export const selectNavLinks = (context, guardCtx) =>
  NAV_LINKS.filter(
    (link) => link.contexts.includes(context) && link.guard(guardCtx),
  );

/**
 * useNavigationLinks(context) — filters NAV_LINKS by rendering context and
 * guard predicates, groups by section, and enriches with icon elements and
 * badge components.
 *
 * @param {"dashboard" | "mobile" | "account-menu" | "account-sidebar"} context
 * @returns {{ sections: Array<{ id: string, label: string|null, links: Array, collapsible: boolean }>, hasAdminLinks: boolean }}
 */
export default function useNavigationLinks(context) {
  const user = useUser();
  const activeInfluencer = useActiveInfluencer();
  const { unsignedCount, unsignedCountRef } = useUnsignedCount(
    activeInfluencer?.route,
  );

  const guardCtx = React.useMemo(
    () => ({ user, activeInfluencer }),
    [user, activeInfluencer],
  );

  const sections = React.useMemo(() => {
    const visibleLinks = selectNavLinks(context, guardCtx);

    // Group by section
    const grouped = {};
    visibleLinks.forEach((link) => {
      const sectionId = link.section;
      if (!grouped[sectionId]) {
        grouped[sectionId] = [];
      }
      grouped[sectionId] = [...grouped[sectionId], link];
    });

    // Build section objects and sort by order
    const sectionEntries = Object.entries(grouped)
      .map(([sectionId, links]) => {
        const meta = SECTION_META[sectionId] ?? {
          label: sectionId,
          order: 99,
          collapsible: false,
        };

        const enrichedLinks = links.map((link) => {
          const IconComponent = getIconComponent(link.iconName);
          const resolvedPath = resolvePath(link, guardCtx);

          const endIcon =
            link.badge === "unsignedCount" && unsignedCount > 0
              ? React.createElement(Badge, {
                  count: unsignedCount,
                  variant: "count",
                  ref: unsignedCountRef,
                })
              : undefined;

          const isThemeV2Icon = IconComponent && !IconComponent.muiName;
          const iconProps = isThemeV2Icon ? {} : { fontSize: "small" };

          return {
            id: link.id,
            label: link.label,
            path: resolvedPath,
            exact: link.exact,
            href: link.href ? resolvedPath : undefined,
            icon: IconComponent
              ? React.createElement(IconComponent, iconProps)
              : null,
            endIcon,
          };
        });

        return {
          id: sectionId,
          label: meta.label,
          order: meta.order,
          links: enrichedLinks,
          collapsible: meta.collapsible,
        };
      })
      .sort((a, b) => a.order - b.order);

    return sectionEntries;
  }, [context, guardCtx, unsignedCount, unsignedCountRef]);

  const hasAdminLinks = React.useMemo(
    () => sections.some((s) => ADMIN_SECTIONS.includes(s.id)),
    [sections],
  );

  return { sections, hasAdminLinks };
}
